import { TrendingUp, TrendingDown, ArrowUpDown, Info } from "lucide-react";

/**
 * Shared KPI card used by Manager, Rep and Admin dashboard views.
 * Extracted from Dashboard.jsx so all three views render the same visual.
 *
 * Iter184 (A02 UX fix) — accepts `tooltip` prop so pairs like
 * "Revenue Generated" and "Cash Collected" can carry their definitions
 * on hover instead of forcing users to guess why two dollar values
 * with different deltas show the same number.
 */
export function KpiCard({ label, value, icon: Icon, format, subtext, color, change, prevValue, compareOn, tooltip }) {
  const formatValue = (val) => {
    if (format === "currency") {
      const num = typeof val === "number" ? val : 0;
      return num >= 1000
        ? `$${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        : `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (format === "percent") {
      return val ? `${Number(val).toFixed(1)}%` : "—";
    }
    return val?.toLocaleString() || 0;
  };

  const renderDelta = () => {
    if (!compareOn) return null;
    // Iter184 (A08) — null change ≠ "0% no change". When previous is
    // zero we render an em-dash so the user sees "no comparison
    // possible" instead of a fabricated 100% or 0%.
    if (change === undefined || change === null) {
      return (
        <div
          className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/5"
          data-testid={`kpi-delta-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <span className="text-xs text-gray-500">— vs prior</span>
          <span className="text-[10px] text-gray-600 ml-auto">no prior data</span>
        </div>
      );
    }
    const isUp = change > 0;
    const isDown = change < 0;
    const colorCls = isUp ? "text-emerald-400" : isDown ? "text-red-400" : "text-gray-500";
    const Arrow = isUp ? TrendingUp : isDown ? TrendingDown : ArrowUpDown;
    return (
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/5" data-testid={`kpi-delta-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <Arrow className={`w-3 h-3 ${colorCls}`} />
        <span className={`text-xs font-medium ${colorCls}`}>
          {isUp ? "+" : ""}{change}%
        </span>
        <span className="text-[10px] text-gray-500 ml-auto">
          vs {formatValue(prevValue)}
        </span>
      </div>
    );
  };

  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-[#1A1A1A] border border-white/5 rounded-xl p-3 sm:p-4 lg:p-5 hover:border-white/10 transition-colors group"
    >
      <div className="flex items-start justify-between mb-2 sm:mb-3">
        <span className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-gray-400 flex items-center gap-1">
          {label}
          {tooltip && (
            <Info
              className="w-3 h-3 text-gray-600 hover:text-gray-400 cursor-help"
              title={tooltip}
              data-testid={`kpi-tooltip-${label.toLowerCase().replace(/\s+/g, "-")}`}
            />
          )}
        </span>
        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${color || "text-gray-500"} group-hover:text-emerald-400 transition-colors`} />
      </div>
      <div className={`stat-value text-xl sm:text-2xl lg:text-3xl leading-tight ${color || "text-white"}`}>
        {formatValue(value)}
      </div>
      {subtext && (
        <p className="text-[10px] sm:text-xs text-gray-500 mt-1.5 sm:mt-2">{subtext}</p>
      )}
      {renderDelta()}
    </div>
  );
}
